import { v4 as uuidv4 } from "uuid";
import { ProductEditData, ProductFromServer } from "../../../../types/Product";
import { CONFIG } from "../../../../config";

/**
 * Parent/child ("service for") structure of the quote lines in the EditQuote table.
 *
 * A service line (e.g. Data Uploads, packaging) can point at one top-level line (its
 * product) through `parentKey`. Only one level is allowed. The table always keeps the
 * rows grouped: every top-level row is followed directly by its children. The server
 * keeps the same order (it renumbers `sort`) and maintains the "(for: …)" description
 * suffix itself, so nothing here touches descriptions.
 */

type TreeRow = Pick<ProductEditData, "key" | "parentKey"> & {
  recordType?: string | null;
};

export interface OrderProductBlock {
  // Index of the top-level row.
  start: number;
  // Index of the last row of the block (the top-level row itself when it has no children).
  end: number;
}

export interface ParentOption {
  key: string;
  label: string;
}

const PARENT_LABEL_MAX_LENGTH = 40;

export const orderProductKey = (id: number) => `op-${id}`;

export const newOrderProductKey = () => `new-${uuidv4()}`;

export const isReferralBonusRow = (row: { recordType?: string | null }) =>
  row.recordType === CONFIG.orderProduct.recordType.refferalBonus;

// True when the manager has entered anything into the row.
export const hasRowContent = (row: ProductEditData) =>
  !!(
    row.productId ||
    (row.description && row.description.trim()) ||
    row.quantity ||
    row.unitPrice ||
    row.productOptionString ||
    row.priceTableId
  );

// Heuristic: the front does not know the Data Uploads product id (a server setting),
// so a saved line is treated as Data Uploads by its description.
export const isDataUploadLine = (row: ProductEditData) =>
  !!row.id && /data\s*uploads?/i.test(row.description || "");

export function createEmptyOrderProduct(
  parentKey: string | null = null,
): ProductEditData {
  return {
    key: newOrderProductKey(),
    parentKey,
    description: null,
    priceTableId: null,
    productId: null,
    productOptionString: null,
    quantity: null,
    unitPrice: null,
    isNewProduct: true,
    isForSupplier: true,
    recordType: null,
  };
}

/**
 * Orders rows as: each top-level row, then its children (in their current relative
 * order). A row whose parent is missing, is itself, is a child, or is the referral
 * bonus line becomes top-level (its parentKey is cleared).
 */
export function groupRows<T extends TreeRow>(rows: T[]): T[] {
  const byKey = new Map<string, T>();
  rows.forEach((row) => byKey.set(row.key, row));

  // First pass: rows that are top-level by themselves.
  const isTopLevel = (row: T) =>
    !row.parentKey || row.parentKey === row.key || !byKey.has(row.parentKey);

  const parentKeyOf = (row: T): string | null => {
    if (isReferralBonusRow(row) || isTopLevel(row)) {
      return null;
    }
    const parent = byKey.get(row.parentKey as string) as T;

    return isTopLevel(parent) && !isReferralBonusRow(parent)
      ? (row.parentKey as string)
      : null;
  };

  const childrenByParent = new Map<string, T[]>();
  const topLevel: T[] = [];

  rows.forEach((row) => {
    const parentKey = parentKeyOf(row);

    if (parentKey === null) {
      topLevel.push(
        row.parentKey === null || row.parentKey === undefined
          ? row
          : { ...row, parentKey: null },
      );
      return;
    }

    const list = childrenByParent.get(parentKey) || [];
    list.push(row);
    childrenByParent.set(parentKey, list);
  });

  return topLevel.flatMap((row) => [
    row,
    ...(childrenByParent.get(row.key) || []),
  ]);
}

// Blocks of a grouped list: a top-level row plus the consecutive rows that belong to it.
export function getBlocks<T extends TreeRow>(rows: T[]): OrderProductBlock[] {
  const blocks: OrderProductBlock[] = [];

  rows.forEach((row, index) => {
    const current = blocks[blocks.length - 1];

    if (current && row.parentKey && rows[current.start].key === row.parentKey) {
      current.end = index;
      return;
    }

    blocks.push({ start: index, end: index });
  });

  return blocks;
}

export const getChildren = <T extends TreeRow>(rows: T[], key: string) =>
  rows.filter((row) => row.parentKey === key && row.key !== key);

// Short label of a parent row for pills and the "Service for" select.
export function parentLabel(row: ProductEditData, index: number): string {
  const description = (row.description || "").trim();

  if (!description) {
    return `Line ${index + 1}`;
  }

  return description.length > PARENT_LABEL_MAX_LENGTH
    ? `${description.slice(0, PARENT_LABEL_MAX_LENGTH - 1).trimEnd()}…`
    : description;
}

// A row can be (re)attached only when it is not the referral bonus and has no children.
export const canChooseParent = (rows: ProductEditData[], index: number) => {
  const row = rows[index];

  return (
    !!row && !isReferralBonusRow(row) && !getChildren(rows, row.key).length
  );
};

// Rows that may become the parent of the row at `index`.
export function getParentOptions(
  rows: ProductEditData[],
  index: number,
): ParentOption[] {
  const row = rows[index];

  if (!row) {
    return [];
  }

  return rows
    .map((candidate, candidateIndex) => ({ candidate, candidateIndex }))
    .filter(
      ({ candidate }) =>
        candidate.key !== row.key &&
        !candidate.parentKey &&
        !isReferralBonusRow(candidate) &&
        hasRowContent(candidate),
    )
    .map(({ candidate, candidateIndex }) => ({
      key: candidate.key,
      label: parentLabel(candidate, candidateIndex),
    }));
}

export function setRowParent(
  rows: ProductEditData[],
  index: number,
  parentKey: string | null,
): ProductEditData[] {
  if (!rows[index] || (parentKey !== null && !canChooseParent(rows, index))) {
    return rows;
  }

  const isValidParent =
    parentKey === null ||
    getParentOptions(rows, index).some((option) => option.key === parentKey);

  if (!isValidParent) {
    return rows;
  }

  return groupRows(
    rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, parentKey } : row,
    ),
  );
}

// Inserts an empty service row at the end of the parent's block.
export function addServiceRow(
  rows: ProductEditData[],
  parentKey: string,
): ProductEditData[] {
  const block = getBlocks(rows).find(
    (current) => rows[current.start].key === parentKey,
  );

  if (!block) {
    return rows;
  }

  const result = [...rows];
  result.splice(block.end + 1, 0, createEmptyOrderProduct(parentKey));

  return result;
}

/**
 * Deletes the row at `index`. When it has service lines, `withChildren` removes them
 * too; otherwise they are detached and become top-level lines.
 */
export function deleteRow(
  rows: ProductEditData[],
  index: number,
  withChildren: boolean,
): ProductEditData[] {
  const row = rows[index];

  if (!row) {
    return rows;
  }

  const result = rows
    .filter(
      (current, currentIndex) =>
        currentIndex !== index && !(withChildren && current.parentKey === row.key),
    )
    .map((current) =>
      current.parentKey === row.key ? { ...current, parentKey: null } : current,
    );

  return groupRows(result);
}

/**
 * Drag & drop. `oldIndex` is the dragged row, `newIndex` the row it was dropped on
 * (an index >= rows.length means "after the last row").
 * - A top-level row moves together with its children and lands on a block boundary:
 *   after the target block when moving down, before it when moving up.
 * - A child row moves only within its parent's block.
 * Without any parents this is the plain "remove at old, insert at new" move.
 */
export function moveRow<T extends TreeRow>(
  rows: T[],
  oldIndex: number,
  newIndex: number,
): T[] {
  const dragged = rows[oldIndex];

  if (!dragged || oldIndex === newIndex) {
    return rows;
  }

  const blocks = getBlocks(rows);
  const blockIndexOf = (rowIndex: number) =>
    blocks.findIndex(
      (block) => rowIndex >= block.start && rowIndex <= block.end,
    );
  const ownBlockIndex = blockIndexOf(oldIndex);
  const ownBlock = blocks[ownBlockIndex];

  if (ownBlock.start !== oldIndex) {
    // A child: clamp the target to its own block.
    const target = Math.min(Math.max(newIndex, ownBlock.start + 1), ownBlock.end);

    if (target === oldIndex) {
      return rows;
    }

    const result = [...rows];
    result.splice(oldIndex, 1);
    result.splice(target, 0, dragged);

    return groupRows(result);
  }

  const targetBlockIndex =
    newIndex >= rows.length ? blocks.length : blockIndexOf(newIndex);

  if (targetBlockIndex === ownBlockIndex || targetBlockIndex < 0) {
    return rows;
  }

  const blockRows = blocks.map((block) => rows.slice(block.start, block.end + 1));
  const [moved] = blockRows.splice(ownBlockIndex, 1);
  // After the removal the same index means "after the target" when moving down and
  // "before the target" when moving up.
  blockRows.splice(targetBlockIndex, 0, moved);

  return groupRows(blockRows.flat());
}

const prettifyProductOption = (stringValues: string) => {
  const lastIndexSign = stringValues.lastIndexOf("_");

  if (lastIndexSign === stringValues.length - 1) {
    stringValues = stringValues.slice(0, lastIndexSign);
  }

  return stringValues;
};

// Server lines -> editable rows: sorted by `sort`, parents linked, grouped.
export function mapServerOrderProducts(
  orderProducts: ProductFromServer[] | null | undefined,
): ProductEditData[] {
  if (!orderProducts) {
    return [];
  }

  const rows = [...orderProducts]
    .sort((a, b) => {
      if (a.sort < b.sort) {
        return -1;
      }
      if (a.sort > b.sort) {
        return 1;
      }

      return 0;
    })
    .map(
      (el): ProductEditData => ({
        id: el.id || undefined,
        key: el.id ? orderProductKey(el.id) : newOrderProductKey(),
        parentKey: el.parentOrderProductId
          ? orderProductKey(el.parentOrderProductId)
          : null,
        description: el.description || "",
        priceTableId: el.priceTable?.id ?? null,
        productId: el.product?.id ?? null,
        productOptionString: el.productOptionsString
          ? prettifyProductOption(el.productOptionsString)
          : null,
        quantity: el.quantity ?? null,
        unitPrice: el.unitPrice ?? null,
        isNewProduct: false,
        isForSupplier: el.isForSupplier ?? true,
        recordType: el.recordType ?? null,
      }),
    );

  return groupRows(rows);
}

// Pads the list with blank rows up to `minRows` (the editable table shows 10 rows).
export function withFillerRows(
  rows: ProductEditData[],
  minRows = 10,
): ProductEditData[] {
  if (rows.length >= minRows) {
    return rows;
  }

  return [
    ...rows,
    ...Array.from({ length: minRows - rows.length }, () =>
      createEmptyOrderProduct(),
    ),
  ];
}
